import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { AddToCartOptions, CartContextType, CartItem, Product } from '../types'
import { cartApi, CartResponse, CartItemResponse } from '../services/cartApi'
import { useAuth } from './AuthContext'
import { productsApi } from '../services/productsApi'

const PRODUCT_PLACEHOLDER_IMAGE = 'https://via.placeholder.com/400x400?text=Product'

const CartContext = createContext<CartContextType | undefined>(undefined)

export function useCart(): CartContextType {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

interface CartProviderProps {
  children: ReactNode
}

const normalizeLocalItems = (items: CartItem[]): CartItem[] => {
  return items
    .map((item): CartItem | null => {
      if (!item.id) {
        return null
      }
      const sellerProductId = item.sellerProductId ?? item.id
      const unitPrice = item.unitPrice ?? item.price ?? 0
      const quantity = item.quantity ?? 0

      return {
        ...item,
        price: unitPrice,
        unitPrice,
        lineTotal: item.lineTotal ?? unitPrice * quantity,
        cartItemId: item.cartItemId ?? `local-${sellerProductId}`,
        sellerProductId,
        currency: item.currency ?? 'USD'
      }
    })
    .filter((item): item is CartItem => item !== null)
}

export function CartProvider({ children }: CartProviderProps) {
  const [cart, setCart] = useState<CartItem[]>([])
  const { authState } = useAuth()

  const mergeGuestCartIntoAccount = useCallback(async () => {
    const savedCart = localStorage.getItem('cart')
    if (!savedCart) return

    try {
      const parsed: CartItem[] = JSON.parse(savedCart)
      const normalized = normalizeLocalItems(parsed)

      if (normalized.length === 0) {
        localStorage.removeItem('cart')
        return
      }

      for (const item of normalized) {
        try {
          await cartApi.addToCart({
            productId: item.id,
            quantity: item.quantity,
            sellerProductId: item.sellerProductId,
            sellerId: item.sellerId
          })
        } catch (error) {
          console.error('Failed to merge cart item', item.cartItemId, error)
        }
      }
      localStorage.removeItem('cart')
    } catch (error) {
      console.error('Failed to merge guest cart into account', error)
    }
  }, [])

  // Load cart from backend when user is authenticated
  // Save cart to localStorage for non-authenticated users
  useEffect(() => {
    if (!authState.isAuthenticated) {
      localStorage.setItem('cart', JSON.stringify(cart))
    }
  }, [cart, authState.isAuthenticated])

  const hydrateCartItems = useCallback(async (items: CartItem[]): Promise<CartItem[]> => {
    const uniqueProductIds = Array.from(
      new Set(
        items
          .map(item => item.id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    )

    if (uniqueProductIds.length === 0) {
      return items
    }

    const productMap = new Map<string, Product>()

    await Promise.all(
      uniqueProductIds.map(async id => {
        try {
          const product = await productsApi.getProductById(id)
          productMap.set(id, product)
        } catch (error) {
          console.error('Failed to hydrate cart item product', id, error)
        }
      })
    )

    if (productMap.size === 0) {
      return items
    }

    return items.map(item => {
      const product = productMap.get(item.id)
      if (!product) {
        return item
      }

      return {
        ...item,
        name: item.name || product.name,
        image: product.image || item.image,
        imageAltText: product.imageAltText ?? item.imageAltText,
        description: item.description || product.description,
        category: item.category || product.category,
        currency: item.currency || product.currency,
        sellerBadges: item.sellerBadges && item.sellerBadges.length > 0
          ? item.sellerBadges
          : product.sellerBadges ?? [],
        sellerLogoUrl: item.sellerLogoUrl ?? product.sellerLogoUrl ?? null
      }
    })
  }, [])

  const loadCart = useCallback(async () => {
    if (!authState.isAuthenticated) return

    try {
      const response = await cartApi.getCart()
      const converted = convertCartResponse(response)
      const hydrated = await hydrateCartItems(converted)
      setCart(hydrated)
    } catch (error) {
      console.error('Failed to load cart:', error)
    }
  }, [authState.isAuthenticated, hydrateCartItems])

  useEffect(() => {
    const synchroniseCart = async () => {
      if (authState.isAuthenticated) {
        await mergeGuestCartIntoAccount()
        await loadCart()
      } else {
        const savedCart = localStorage.getItem('cart')
        if (savedCart) {
          try {
            const parsed: CartItem[] = JSON.parse(savedCart)
            setCart(normalizeLocalItems(parsed))
          } catch (error) {
            console.error('Failed to parse local cart:', error)
            setCart([])
          }
        } else {
          setCart([])
        }
      }
    }

    void synchroniseCart()
  }, [authState.isAuthenticated, loadCart, mergeGuestCartIntoAccount])

  const convertCartResponse = (response: CartResponse): CartItem[] => {
    const currency = response.currency ?? 'USD'

    return response.items.map((item: CartItemResponse) => {
      const unitPrice = Number(item.unitPrice ?? 0)
      const quantity = Number(item.quantity ?? 0)
      const lineTotal = Number(item.lineTotal ?? unitPrice * quantity)
      const sellerBadges = Array.isArray(item.sellerBadges)
        ? item.sellerBadges.filter((badge): badge is string => typeof badge === 'string' && badge.length > 0)
        : []

      return {
        id: item.productId,
        name: item.productName ?? 'Product',
        price: unitPrice,
        description: '',
        category: 'General',
        image: PRODUCT_PLACEHOLDER_IMAGE,
        inStock: quantity > 0,
        rating: 0,
        reviews: 0,
        currency,
        status: 'ACTIVE',
        sellerId: item.sellerId ?? undefined,
        slug: item.productSlug ?? undefined,
        inventoryQuantity: quantity,
        attributes: undefined,
        imageAltText: item.productName ?? undefined,
        quantity,
        cartItemId: item.id,
        sellerProductId: item.sellerProductId ?? undefined,
        sellerName: item.sellerName ?? undefined,
        sellerRating: item.sellerRating ?? undefined,
        unitPrice,
        lineTotal,
        shippingDays: null,
        condition: null,
        sellerLogoUrl: item.sellerLogoUrl ?? null,
        sellerBadges
      }
    })
  }

  const addToCart = async (product: Product, options: AddToCartOptions): Promise<void> => {
    const { seller, quantity = 1 } = options

    if (!seller) {
      throw new Error('Seller selection is required to add to cart')
    }

    if (authState.isAuthenticated) {
      try {
        const response = await cartApi.addToCart({
          productId: product.id,
          quantity,
          sellerProductId: seller.sellerProductId,
          sellerId: seller.sellerId
        })
        const converted = convertCartResponse(response)
        const hydrated = await hydrateCartItems(converted)
        setCart(hydrated)
      } catch (error) {
        console.error('Failed to add to cart:', error)
        throw error
      }
    } else {
      const sellerProductKey =
        seller.sellerProductId ?? `${product.id}-${seller.sellerId ?? 'marketplace'}`
      const localCartItemId = `local-${product.id}-${sellerProductKey}`
      const unitPrice = seller.price ?? product.price

      setCart(prevCart => {
        const existingItem = prevCart.find(item => item.cartItemId === localCartItemId)

        if (existingItem) {
          return prevCart.map(item =>
            item.cartItemId === localCartItemId
              ? {
                  ...item,
                  quantity: item.quantity + quantity,
                  lineTotal: (item.quantity + quantity) * item.unitPrice
                }
              : item
          )
        }

        return [
          ...prevCart,
          {
            ...product,
            price: unitPrice,
            unitPrice,
            lineTotal: unitPrice * quantity,
            quantity,
            cartItemId: localCartItemId,
            sellerProductId: sellerProductKey,
            sellerName: seller.sellerName,
            sellerRating: seller.sellerRating,
            shippingDays: seller.shippingDays ?? null,
            condition: seller.condition ?? null,
            currency: product.currency ?? seller.currency ?? 'USD',
            sellerBadges: seller.badges ?? product.sellerBadges ?? [],
            sellerLogoUrl: product.sellerLogoUrl ?? null
          }
        ]
      })
    }
  }

  const removeFromCart = async (cartItemId: string): Promise<void> => {
    if (authState.isAuthenticated) {
      try {
        const target = cart.find(item => (item.cartItemId ?? item.id) === cartItemId)
        const productId = target?.id ?? cartItemId
        const response = await cartApi.removeFromCart(productId, {
          sellerProductId: target?.sellerProductId
        })
        const converted = convertCartResponse(response)
        const hydrated = await hydrateCartItems(converted)
        setCart(hydrated)
      } catch (error) {
        console.error('Failed to remove from cart:', error)
        throw error
      }
    } else {
      setCart(prevCart => prevCart.filter(item => item.cartItemId !== cartItemId))
    }
  }

  const updateQuantity = async (cartItemId: string, quantity: number): Promise<void> => {
    if (quantity <= 0) {
      await removeFromCart(cartItemId)
      return
    }
    
    if (authState.isAuthenticated) {
      try {
        const target = cart.find(item => (item.cartItemId ?? item.id) === cartItemId)
        if (!target) {
          console.error('Cannot update quantity: cart item not found for id', cartItemId)
          return
        }

        const response = await cartApi.updateCartItem({
          productId: target.id,
          quantity,
          sellerProductId: target.sellerProductId,
          sellerId: target.sellerId
        })
        const converted = convertCartResponse(response)
        const hydrated = await hydrateCartItems(converted)
        setCart(hydrated)
      } catch (error) {
        console.error('Failed to update quantity:', error)
        throw error
      }
    } else {
      setCart(prevCart =>
        prevCart.map(item =>
          item.cartItemId === cartItemId
            ? { ...item, quantity, lineTotal: item.unitPrice * quantity }
            : item
        )
      )
    }
  }

  const clearCart = async (): Promise<void> => {
    if (authState.isAuthenticated) {
      try {
        const response = await cartApi.clearCart()
        const converted = convertCartResponse(response)
        const hydrated = await hydrateCartItems(converted)
        setCart(hydrated)
      } catch (error) {
        console.error('Failed to clear cart:', error)
        throw error
      }
    } else {
      setCart([])
    }
  }

  const getCartTotal = (): number => {
    return cart.reduce((total, item) => total + item.unitPrice * item.quantity, 0)
  }

  const getCartCount = (): number => {
    return cart.reduce((count, item) => count + item.quantity, 0)
  }

  const value: CartContextType = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}
